// Single source of truth for the Cost Report Analysis distribution curve —
// used by BOTH the facility tab (CostReportAnalysisTab) and the owner-group tab
// (OwnerGroupCostReportTab) so they never drift.
//
// The curve is the slope (density) of a monotone-cubic (PCHIP) interpolation of
// the cumulative distribution, fit through the ROBUST quantiles below. Because
// the CDF is smooth and monotone, the density is smooth by construction (no step
// artifacts) and the curve passes through the real data points.
//
// We deliberately use only p10·p30·p50·p70·p90 (evenly spaced every 20 pctile
// points): p25/p75 cluster against p30/p70 and dent the curve, and the max/p100
// is dominated by data-entry errors (admin p100 nationally ≈ $535k/res-day,
// ~5,600x p90). p10–p90 are robust to those errors. Beyond p90 the curve decays
// smoothly — it never reaches toward the contaminated max.

export interface Quantiles {
  p10: number | null;
  p30: number | null;
  median: number | null;
  p70: number | null;
  p90: number | null;
}

const ROBUST: [number, keyof Quantiles][] = [
  [10, 'p10'], [30, 'p30'], [50, 'median'], [70, 'p70'], [90, 'p90'],
];

export function curveFromQuantiles(
  q: Quantiles, domain: number, W: number, H: number, n = 100,
): { line: string; area: string } | null {
  // CDF knots: (value, cumulative fraction), anchored at (0, 0), strictly increasing.
  const xs: number[] = [0]; const ys: number[] = [0];
  for (const [pct, key] of ROBUST) {
    const v = q[key];
    if (v != null && v > xs[xs.length - 1]) { xs.push(v); ys.push(pct / 100); }
  }
  if (xs.length < 3) return null;

  const m = xs.length - 1;
  const h: number[] = [], del: number[] = [];
  for (let i = 0; i < m; i++) { h.push(xs[i + 1] - xs[i]); del.push((ys[i + 1] - ys[i]) / (xs[i + 1] - xs[i])); }

  // Fritsch–Carlson tangents → monotone (non-overshooting) cubic CDF.
  const tan: number[] = new Array(m + 1).fill(0);
  tan[0] = del[0]; tan[m] = del[m - 1];
  for (let i = 1; i < m; i++) {
    if (del[i - 1] * del[i] <= 0) { tan[i] = 0; }
    else { const w1 = 2 * h[i] + h[i - 1], w2 = h[i] + 2 * h[i - 1]; tan[i] = (w1 + w2) / (w1 / del[i - 1] + w2 / del[i]); }
  }

  const top = xs[m];                       // p90 — last trusted knot
  const tailScale = Math.max(1e-6, h[m - 1]);
  // density(x) = derivative of the cubic-Hermite CDF (smooth, >= 0). Beyond p90, decay.
  const density = (x: number): number => {
    if (x <= 0) return 0;
    if (x >= top) return Math.max(0, tan[m]) * Math.exp(-(x - top) / tailScale);
    let i = 0; while (i < m - 1 && x > xs[i + 1]) i++;
    const t = (x - xs[i]) / h[i];
    const dh00 = 6 * t * t - 6 * t, dh10 = 3 * t * t - 4 * t + 1, dh01 = -6 * t * t + 6 * t, dh11 = 3 * t * t - 2 * t;
    return Math.max(0, (ys[i] * dh00 + ys[i + 1] * dh01) / h[i] + tan[i] * dh10 + tan[i + 1] * dh11);
  };

  let s: number[] = [];
  for (let i = 0; i <= n; i++) s.push(density((i / n) * domain));
  for (let pass = 0; pass < 2; pass++)     // clean sampling jaggies
    s = s.map((_, i) => (s[Math.max(0, i - 1)] + 2 * s[i] + s[Math.min(s.length - 1, i + 1)]) / 4);

  const ymax = Math.max(...s) || 1;
  let line = '';
  for (let i = 0; i <= n; i++) {
    const px = (i / n) * W, py = H - (s[i] / ymax) * H * 0.9;
    line += `${i ? 'L' : 'M'} ${px.toFixed(1)} ${py.toFixed(1)} `;
  }
  return { line, area: `M 0 ${H} ${line.replace('M', 'L')} L ${W} ${H} Z` };
}
